// The nav tree, in one place. SideNav.vue renders it; header.vue uses it to resolve the
// current page title in sidebar mode. Gates are keys on the object returned by
// computeNavPermissions() — same role rules header.vue has always applied, lifted verbatim.
//
// Item shapes:
//   { label, to, icon, gate? }                         internal route
//   { label, href, icon, gate?, external: true }       external link (new tab)
//   { label, icon, gate?, group: true, children: [...] }
//   children may include { section: "Heading" } dividers (used by swiftComply)

function getRolesFromStorage(key) {
  let raw = null
  try {
    raw = window?.localStorage?.getItem(key)
  } catch (e) {}
  if (!raw) return []

  const toList = v =>
    String(v || "")
      .split(",")
      .map(x => String(x || "").trim())
      .filter(Boolean)

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(x => String(x || "").trim()).filter(Boolean)
    return toList(parsed)
  } catch {
    return toList(raw)
  }
}

function rolesInclude(roles, role) {
  const target = String(role || "").toLowerCase().trim()
  return (roles || []).map(x => String(x || "").toLowerCase().trim()).includes(target)
}

export function computeNavPermissions() {
  const roles = getRolesFromStorage("active_user_roles")
  const isCorp = rolesInclude(roles, "Corp")
  const canViewRepricedLocks = rolesInclude(roles, "Compensation Admin")

  // A Branch or Division Manager gets ONE item in swiftLocks: Revenue Analysis, scoped by the
  // API — a branch manager to their own branch, a division manager to every branch in their
  // division. This decides menu VISIBILITY only; the scope itself is enforced server-side
  // (SwiftAppAIService.GetRevenueAlertAnalysis), because a scope a browser could edit is not a
  // scope.
  //
  // A division manager's authority comes from the Reporting division_managers roster, not from
  // this role — so the API serves them whether or not the role is assigned. The role is what
  // puts the link in front of them.
  const canViewRevenueAnalysis = canViewRepricedLocks
    || rolesInclude(roles, "Branch Manager")
    || rolesInclude(roles, "Division Manager")

  return {
    isCorp,
    isPostClosing: rolesInclude(roles, "Post Closer") || isCorp,
    canViewRepricedLocks,
    canViewRevenueAnalysis,
    // The group opens for anyone with at least one item in it.
    canViewLocks: canViewRepricedLocks || canViewRevenueAnalysis,
    canViewHmda: isCorp || rolesInclude(roles, "HMDA"),
    // swiftAnalyze. Mirror any change in header.vue applyNavPermissions().
    isAdmin: rolesInclude(roles, "Admin")
  }
}

export function applicationAdminLink() {
  return `${process.env.VUE_APP_LOANLAUNCH_APP_ENDPOINT}/applicationAdmin`
}

// "Tools" (was "Me"): the signed-in LO's profile, website and notifications, plus Application
// Admin for corp users. Admin is gated per child, so non-corp users just see the first three.
const websiteNavItem = {
  label: "Tools",
  icon: "fa-light fa-toolbox",
  group: true,
  children: [
    { label: "My Profile", to: "/my-profile" },
    { label: "My Website", to: "/LORedirect" },
    { label: "Notification Management", to: "/NotificationManagement" },
    { label: "Application Admin", href: applicationAdminLink, gate: "isCorp", external: true }
  ]
}

export const NAV_ITEMS = [
  { label: "Command Center", to: "/", icon: "fa-light fa-grid-2" },
  { label: "Applications", to: "/Applications", icon: "fa-light fa-users" },
  // Scenario Mode — no loanId. Opens in the same tab now (was: external tab) so it behaves
  // like every other internal nav item; the per-loan button in the Pipeline grid instead opens
  // as a left-side flyout so users don’t lose their place on the grid (see ProcessingV2.vue).
  { label: "Pricing Engine", to: "/polly-pricer", icon: "fa-light fa-calculator" },
  { label: "Pipeline", to: "/Processing", icon: "fa-light fa-table-cells-large" },
  { label: "Leaderboard", to: "/Leaderboard", icon: "fa-light fa-trophy" },
  // Ported from CGS2022. Ungated — every LO checks turn times.
  { label: "Turn Times", to: "/turn-times", icon: "fa-light fa-stopwatch" },
  // LoRedirect.vue — the LO picks and manages their own marketing site. No loanId, no
  // role gate: every LO manages their own. Route is /LORedirect; isPathActive lowercases
  // both sides, so the /loredirect URL matches too.
  websiteNavItem,
  { label: "swiftPC", to: "/PostCloseConnect", icon: "fa-light fa-desktop", gate: "isPostClosing" },
  { label: "swiftUW", to: "/Insights", icon: "fa-light fa-building", gate: "isCorp" },
  { label: "swiftAnalyze", to: "/swift-analyze", icon: "fa-light fa-file-magnifying-glass", gate: "isAdmin" },
  {
    label: "swiftLocks",
    icon: "fa-light fa-lock",
    gate: "canViewLocks",
    group: true,
    // Children ARE gate-filtered now (filterByPermissions recurses), because a Branch Manager
    // may open this group for Revenue Analysis alone and must not be handed the lock desk's
    // pages with it. A child with no gate stays visible to anyone who can see the group.
    children: [
      { label: "Work Queue", to: "/lock-desk-queue", gate: "canViewRepricedLocks" },
      // Reporting on the revenue alerts the Work Queue holds — the alerts themselves are
      // worked from the queue, this reads them back.
      { label: "Revenue Analysis", to: "/revenue-analysis", gate: "canViewRevenueAnalysis" },
      // How long locks sit unvalidated. The page itself is ConcessionsAdmin-only (server-side);
      // that role is not in active_user_roles, so the link rides the lock desk gate like Concessions.
      { label: "Validations", to: "/validations", gate: "canViewRepricedLocks" },
      { label: "Lock Reprice", to: "/reprice", gate: "canViewRepricedLocks" },
      { label: "Lock Extensions", to: "/extensions", gate: "canViewRepricedLocks" },
      { label: "Extension Request", to: "/lock-extension-request", gate: "canViewRepricedLocks" },
      { label: "Price Sheet", to: "/loan-price-sheet", gate: "canViewRepricedLocks" },
      // Moved in here from the top level, where it was ungated. It rides the lock desk gate,
      // and the page still self-gates off the Polly roster on top of that.
      { label: "Concessions", to: "/concessions", gate: "canViewRepricedLocks" }
    ]
  },
  {
    // Ungated on purpose: these are generic arithmetic tools with no loan data behind them,
    // so there is nothing here to scope by role. More children get added as we build them.
    label: "Calculators",
    icon: "fa-light fa-abacus",
    group: true,
    children: [
      { label: "Payment Calculator", to: "/calculators/payment" },
      { label: "Buydown Calculator", to: "/calculators/buydown" },
      { label: "Debt Consolidation", to: "/calculators/debt-consolidation" },
      { label: "Cash-Out Refi Scenario", to: "/calculators/cash-out-refi" },
      { label: "Scenario Builder", to: "/calculators/scenario-builder" }
    ]
  },
  {
    label: "swiftComply",
    icon: "fa-light fa-shield-check",
    gate: "canViewHmda",
    group: true,
    children: [
      { section: "Disclosures" },
      { label: "ICD", to: "/ICD" },
      { label: "Disclosure Audit", to: "/disclosure-audit" },
      { section: "Loan Status" },
      { label: "Loan Status", to: "/hmda-withdrawals" },
      { section: "Insuring" },
      { label: "Closing Packages", to: "/ai-insuring" },
      { label: "Upload FHA MIP", to: "/Insuring" },
      { label: "FHA MIP Payments", to: "/fha-mip-payments" },
      { section: "Post Consummation" },
      { label: "PCCD", to: "/pccd" }
    ]
  },
  {
    label: "swiftAccounting",
    icon: "fa-light fa-calculator-simple",
    gate: "canViewAccounting",
    group: true,
    children: [
      { section: "Loan Vision" },
      { label: "Loans Funded", to: "/loans-funded" }
    ]
  }
]

// Routes are case-insensitive in vue-router; compare lowercase. "/" must be exact or it
// would light up on every page.
export function isPathActive(routePath, to) {
  const path = String(routePath || "").toLowerCase()
  const target = String(to || "").toLowerCase()
  if (target === "/") return path === "/"
  return path === target || path.startsWith(target + "/")
}

// Filters the tree, groups included. A group's children are filtered too, and a group left
// with nothing navigable is dropped rather than rendered as an empty menu — that is what lets a
// gate on a single child stand on its own (see swiftLocks / Revenue Analysis).
//
// Returns new objects: NAV_ITEMS is module state shared by both renderers and is never mutated.
export function filterByPermissions(items, perms) {
  const allowed = i => !i.gate || perms[i.gate]

  return items.filter(allowed).map(item => {
    if (!item.group) return item

    const children = (item.children || []).filter(allowed)

    // Section headings carry no gate, so a group can survive on headings alone. Require a real
    // destination before keeping it.
    return children.some(c => c.to || c.href) ? { ...item, children } : null
  }).filter(Boolean)
}

// Page title for the top bar: deepest matching leaf, with its group's icon as fallback.
export function resolvePageTitle(routePath) {
  for (const item of NAV_ITEMS) {
    if (item.group) {
      const hit = item.children.find(c => c.to && isPathActive(routePath, c.to))
      if (hit) return { label: hit.label, parent: item.label, icon: item.icon }
    } else if (item.to && isPathActive(routePath, item.to)) {
      return { label: item.label, parent: null, icon: item.icon }
    }
  }
  return null
}